import type { DocumentRevision } from '@/entities/Document';

import { useDocumentListTableDelete } from './useDocumentListTableDelete';
import { useDocumentListTableOperations } from './useDocumentListTableOperations';

interface UseDocumentListTableOptions {
  revisions: () => DocumentRevision[];
  onDelete: (deletedIds: number[]) => void;
  onSort: (sortCamp: string, orderFlow: string) => void;
  onFilter: (filters: Record<string, any>) => void;
  onFilterClicked: () => void;
}

export function useDocumentListTable(options: UseDocumentListTableOptions) {
  const { revisions, onDelete, onSort, onFilter, onFilterClicked } = options;

  // Operations (selection, sort, filter)
  const operations = useDocumentListTableOperations({
    revisions,
    onSort,
    onFilter,
    onFilterClicked,
  });

  // Delete logic
  const deleteLogic = useDocumentListTableDelete({
    selectedDocuments: operations.selectedDocuments,
    deselectAllDocuments: operations.deselectAllDocuments,
    onDelete,
  });

  const handleSingleDelete = (id: number) => {
    operations.deselectAllDocuments();
    operations.handleToggleSelection(id);
    deleteLogic.openDeleteConfirmation();
  };

  return {
    // Selection
    selectedCount: operations.selectedCount,
    hasSelectedDocuments: operations.hasSelectedDocuments,
    isDocumentSelected: operations.isDocumentSelected,
    selectedDocuments: operations.selectedDocuments,
    handleToggleSelection: operations.handleToggleSelection,
    handleToggleAll: operations.handleToggleAll,
    allSelected: operations.allSelected,

    // Sort/Filter
    handleSortOptions: operations.handleSortOptions,
    handleFilter: operations.handleFilter,
    handleFilterClicked: operations.handleFilterClicked,
    orderOptions: operations.orderOptions,

    // Delete
    handleOpenDeleteConfirmation: deleteLogic.handleOpenDeleteConfirmation,
    handleSingleDelete,
  };
}
